import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common";
import { Camera, Crosshair, Loader2, Upload, CheckCircle2, AlertTriangle, Info, AlertCircle } from "@/components/icons";
import { fetchCNPJ } from "@/lib/lookup";
import { validCNPJ } from "@/lib/formMasks";

const INPUT = "w-full h-11 px-3.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40";
const LABEL = "block text-sm font-medium text-slate-700 mb-1.5";

function _fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR");
  } catch { return ""; }
}

export default function Captacao() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSeller = user?.role === "seller";
  const [vendedores, setVendedores] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [coords, setCoords] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ vendedor_id: "", nome: "", nome_fantasia: "", cnpj: "", telefone: "", endereco: "", observacao: "" });
  const [dupAviso, setDupAviso] = useState(null); // { proprio: bool, nome, owner_name, expira_em, cliente_id }
  const [dupLoading, setDupLoading] = useState(false);
  const dupSeq = useRef(0);
  // LOTE CN-1 — mensagens claras para a consulta de CNPJ no "Foto na rua".
  // CNPJ segue opcional; o envio da foto nunca é bloqueado pela consulta.
  const [cnpjMsg, setCnpjMsg] = useState("idle"); // idle|busy|ok|invalid|not_found|situacao_alerta|unavailable
  const [cnpjSource, setCnpjSource] = useState(null);
  const [cnpjSituacao, setCnpjSituacao] = useState(null); // "BAIXADA" | "INAPTA" | ...

  const onCnpjBlur = async () => {
    const v = (form.cnpj || "").trim();
    if (!v) { setCnpjMsg("idle"); setCnpjSource(null); setCnpjSituacao(null); return; }
    if (!validCNPJ(v)) { setCnpjMsg("invalid"); setCnpjSource(null); setCnpjSituacao(null); return; }
    setCnpjMsg("busy"); setCnpjSource(null); setCnpjSituacao(null);
    const bapi = await fetchCNPJ(v);
    const st = bapi?.status || "unavailable";
    if (st === "ok" || st === "situacao_alerta") {
      const d = bapi.data || {};
      setCnpjSource(bapi.source || null);
      setCnpjSituacao(st === "situacao_alerta" ? (bapi.situacao || null) : null);
      setCnpjMsg(st === "situacao_alerta" ? "situacao_alerta" : "ok");
      // Preencher APENAS o nome se estiver vazio (regra do pedido F2).
      setForm((f) => ({
        ...f,
        nome: f.nome || d.empresa || "",
        nome_fantasia: f.nome_fantasia || d.nome_fantasia || "",
      }));
    } else if (st === "not_found") {
      setCnpjMsg("not_found");
    } else if (st === "invalid") {
      setCnpjMsg("invalid");
    } else {
      setCnpjMsg("unavailable");
    }
  };

  useEffect(() => {
    if (isSeller) { setVendedores([]); return; }
    api.get("/captacoes/vendedores").then((r) => setVendedores(r.data || [])).catch(() => setVendedores([]));
  }, [isSeller]);

  const checkDuplicado = async () => {
    const nome = form.nome.trim();
    if (nome.length < 3) { setDupAviso(null); return; }
    const seq = ++dupSeq.current;
    setDupLoading(true);
    try {
      const { data } = await api.get("/clientes/verificar", { params: { nome } });
      if (seq !== dupSeq.current) return; // resposta atrasada — descartar
      const d = (data?.duplicados || [])[0];
      if (d) {
        setDupAviso({
          proprio: d.owner_id === user?.id,
          nome: d.nome, owner_name: d.owner_name,
          expira_em: d.expira_em, cliente_id: d.cliente_id,
        });
      } else {
        setDupAviso(null);
      }
    } catch { /* silencioso — não bloqueia o formulário */ }
    finally { if (seq === dupSeq.current) setDupLoading(false); }
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const getCoords = () => {
    if (!navigator.geolocation) return toast.error("Geolocalização não suportada");
    navigator.geolocation.getCurrentPosition(
      (p) => { setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }); toast.success("Localização anexada"); },
      () => toast.error("Não foi possível obter a localização")
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Anexe uma foto do estabelecimento");
    if (!isSeller && !form.vendedor_id) return toast.error("Selecione o vendedor de destino");
    const cnpjTrim = form.cnpj.trim();
    if (cnpjTrim && !/^\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}$/.test(cnpjTrim)) {
      return toast.error("Se preencher o CNPJ, precisa ter 14 dígitos válidos");
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => {
        if (isSeller && k === "vendedor_id") return; // vendedor: sem destinatário
        fd.append(k, v);
      });
      if (coords) { fd.append("lat", coords.lat); fd.append("lng", coords.lng); }
      const { data } = await api.post("/captacoes", fd);
      if (isSeller) {
        toast.success("Prospect criado no seu funil");
        navigate(data?.cliente?.id ? `/cliente/${data.cliente.id}` : "/pipeline");
      } else {
        toast.success("Prospect enviado ao vendedor!");
        navigate("/potenciais");
      }
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  if (vendedores === null) return null;
  if (!isSeller && vendedores.length === 0)
    return (
      <div>
        <PageHeader title="Foto na rua" />
        <EmptyState icon={Camera} title="Nenhum vendedor disponível" subtitle="Você precisa de vendedores vinculados à sua equipe para enviar o prospect." />
      </div>
    );

  return (
    <div className="max-w-2xl">
      <PageHeader
        title="Foto na rua"
        subtitle={isSeller
          ? "Está na frente do estabelecimento? Foto, nome e localização — vai direto para o seu funil em Prospecção. Complete o CNPJ em até 5 dias."
          : "Está na frente do estabelecimento? Foto, nome e localização — envie para um vendedor trabalhar. Complete o CNPJ em até 5 dias."}
      />
      <form onSubmit={submit} className="bg-card rounded-2xl border border-black/5 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)] space-y-4" data-testid="captacao-form">
        <div>
          <label className={LABEL}>Foto *</label>
          {preview ? (
            <div className="relative rounded-xl overflow-hidden border border-black/5">
              <img src={preview} alt="prévia" className="w-full max-h-64 object-cover" />
              <div className="absolute bottom-2 right-2 flex gap-1.5">
                <label className="bg-white/90 rounded-lg px-3 py-1.5 text-xs font-medium cursor-pointer flex items-center gap-1.5" data-testid="captacao-camera-trocar">
                  <Camera className="w-3.5 h-3.5" /> Câmera
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pickFile} />
                </label>
                <label className="bg-white/90 rounded-lg px-3 py-1.5 text-xs font-medium cursor-pointer flex items-center gap-1.5" data-testid="captacao-galeria-trocar">
                  <Upload className="w-3.5 h-3.5" /> Galeria
                  <input type="file" accept="image/*" className="hidden" onChange={pickFile} />
                </label>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2" data-testid="captacao-file">
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl py-8 cursor-pointer hover:border-brand/40 hover:bg-brand-soft/30 transition-colors" data-testid="captacao-camera">
                  <Camera className="w-7 h-7 text-slate-400" />
                  <span className="text-sm text-slate-500">Tirar com a câmera</span>
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pickFile} />
                </label>
                <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl py-8 cursor-pointer hover:border-brand/40 hover:bg-brand-soft/30 transition-colors" data-testid="captacao-galeria">
                  <Upload className="w-7 h-7 text-slate-400" />
                  <span className="text-sm text-slate-500">Escolher da galeria</span>
                  <input type="file" accept="image/*" className="hidden" onChange={pickFile} />
                </label>
              </div>
            </div>
          )}
        </div>

        {!isSeller && (
          <div>
            <label className={LABEL}>Enviar para *</label>
            <select required value={form.vendedor_id} onChange={(e) => setForm((f) => ({ ...f, vendedor_id: e.target.value }))} className={INPUT} data-testid="captacao-vendedor">
              <option value="">— Selecione o vendedor —</option>
              {vendedores.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className={LABEL}>Nome / empresa vista *</label>
            <input
              required
              value={form.nome}
              onChange={(e) => { setForm((f) => ({ ...f, nome: e.target.value })); if (dupAviso) setDupAviso(null); }}
              onBlur={checkDuplicado}
              className={INPUT}
              data-testid="captacao-nome"
            />
            {dupLoading && (
              <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Verificando na base…
              </p>
            )}
            {dupAviso && (
              dupAviso.proprio ? (
                <div
                  data-testid="captacao-dup-proprio"
                  className="mt-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-900 px-3 py-2 text-xs flex items-start gap-2"
                >
                  <Info className="w-4 h-4 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    Você já tem <b>&ldquo;{dupAviso.nome}&rdquo;</b> na sua carteira.
                    {dupAviso.cliente_id && (
                      <> <Link to={`/cliente/${dupAviso.cliente_id}`} className="underline font-medium">Abrir ficha existente</Link>.</>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  data-testid="captacao-dup-outro"
                  className="mt-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 text-xs flex items-start gap-2"
                >
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    ⚠️ <b>&ldquo;{dupAviso.nome}&rdquo;</b> já está reservado para <b>{dupAviso.owner_name}</b>
                    {dupAviso.expira_em ? <> até <b>{_fmtDate(dupAviso.expira_em)}</b></> : null}.
                    Fale com seu gestor antes de cadastrar.
                  </div>
                </div>
              )
            )}
          </div>
          <div>
            <label className={LABEL}>Nome fantasia (opcional)</label>
            <input value={form.nome_fantasia} onChange={(e) => setForm((f) => ({ ...f, nome_fantasia: e.target.value }))} className={INPUT} data-testid="captacao-nome-fantasia" placeholder="Preenchido pela Receita quando houver" />
          </div>
          <div>
            <label className={LABEL}>CNPJ (opcional)</label>
            <input value={form.cnpj} onChange={(e) => { setForm((f) => ({ ...f, cnpj: e.target.value })); setCnpjMsg("idle"); }} onBlur={onCnpjBlur} className={INPUT} data-testid="captacao-cnpj" placeholder="00.000.000/0000-00" inputMode="numeric" />
            {cnpjMsg === "busy" && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1" data-testid="captacao-cnpj-busy"><Loader2 className="w-3 h-3 animate-spin" /> Consultando a Receita…</p>}
            {cnpjMsg === "ok" && <p className="text-xs text-ok mt-1 flex items-center gap-1" data-testid="captacao-cnpj-ok"><CheckCircle2 className="w-3 h-3" /> Dados preenchidos pela Receita Federal{cnpjSource ? ` (${cnpjSource})` : ""}.</p>}
            {cnpjMsg === "invalid" && <p className="text-xs text-bad mt-1" data-testid="captacao-cnpj-invalid">CNPJ inválido (dígito verificador não confere).</p>}
            {cnpjMsg === "not_found" && <p className="text-xs text-warn mt-1" data-testid="captacao-cnpj-notfound">CNPJ não encontrado na Receita.</p>}
            {cnpjMsg === "situacao_alerta" && cnpjSituacao && <p className="text-xs text-warn mt-1" data-testid="captacao-cnpj-situacao">⚠️ Atenção: CNPJ com situação <b>{cnpjSituacao}</b> na Receita.</p>}
            {cnpjMsg === "unavailable" && (
              <div className="mt-1 flex items-center gap-2 text-xs text-warn" data-testid="captacao-cnpj-unavailable">
                <AlertCircle className="w-3 h-3" />
                <span>Serviço da Receita indisponível agora. Preencha manualmente ou tente de novo.</span>
                <button type="button" onClick={onCnpjBlur} data-testid="captacao-cnpj-retry" className="font-semibold text-brand hover:underline">Tentar de novo</button>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-1">
              {isSeller
                ? "O lead já fica reservado para você por 90 dias a partir de agora. Complete o CNPJ em até 5 dias para manter a reserva."
                : "O lead já fica reservado para o vendedor por 90 dias a partir de agora. O CNPJ deve ser completado em até 5 dias."}
            </p>
          </div>
          <div><label className={LABEL}>Telefone visto</label><input value={form.telefone} onChange={(e) => setForm((f) => ({ ...f, telefone: e.target.value }))} className={INPUT} inputMode="tel" /></div>
        </div>
        <div><label className={LABEL}>Local / endereço</label><input value={form.endereco} onChange={(e) => setForm((f) => ({ ...f, endereco: e.target.value }))} className={INPUT} /></div>
        <div><label className={LABEL}>Observação</label><textarea value={form.observacao} onChange={(e) => setForm((f) => ({ ...f, observacao: e.target.value }))} rows={2} className={`${INPUT} h-auto py-2.5`} /></div>

        <button type="button" onClick={getCoords} className="flex items-center gap-2 text-sm text-slate-600 hover:text-navy">
          {coords ? <CheckCircle2 className="w-4 h-4 text-ok" /> : <Crosshair className="w-4 h-4" />}
          {coords ? "Localização anexada" : "Anexar minha localização"}
        </button>

        <div className="flex justify-end pt-2">
          <button type="submit" disabled={saving} data-testid="send-captacao-btn" className="h-11 px-6 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} {isSeller ? "Cadastrar prospect" : "Enviar prospect"}
          </button>
        </div>
      </form>
    </div>
  );
}
